import { useCallback, useEffect, useState } from "react";
import enqApi, { ENQ_TOKEN_KEY } from "@/lib/enquiryApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ClipboardList, LogOut, Plus, ArrowLeft, CheckCircle2, AlertTriangle,
  Loader2, FileText, Building2, Calendar, Hash, Eye,
} from "lucide-react";
import { DeptCombobox, Combobox, fmtDMY, isPastDate, StatusBadge } from "./parts";

const EMPTY_REG = { name: "", company: "", mobile: "", email: "", password: "", city: "", state: "", state_code: "" };
const EMPTY_ENQ = { type: "government", department: "", tender_id: "", due_date: "", remarks: "" };

export default function CustomerPortal() {
  const [booting, setBooting] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [view, setView] = useState("auth"); // auth | list | new | confirm | detail

  const loadMe = useCallback(async () => {
    if (!localStorage.getItem(ENQ_TOKEN_KEY)) { setBooting(false); return; }
    try {
      const { data } = await enqApi.get("/auth/me");
      setCustomer(data); setView("list");
    } catch {
      localStorage.removeItem(ENQ_TOKEN_KEY);
    } finally { setBooting(false); }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const onAuthed = (data) => {
    localStorage.setItem(ENQ_TOKEN_KEY, data.token);
    setCustomer(data.customer); setView("list");
  };
  const logout = () => {
    localStorage.removeItem(ENQ_TOKEN_KEY);
    setCustomer(null); setView("auth");
  };

  if (booting) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-[#22a722]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#22a722] text-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-white/20 grid place-items-center">
              <ClipboardList className="w-5 h-5" />
            </div>
            <div className="leading-tight">
              <p className="font-display font-bold text-base">DVEPL Enquiry Portal</p>
              <p className="text-[11px] text-white/80">Submit & track your tender enquiries</p>
            </div>
          </div>
          {customer && (
            <button data-testid="enq-logout-btn" onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm bg-white/15 hover:bg-white/25 px-3 py-1.5 rounded-lg transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {view === "auth" && <AuthPanel onAuthed={onAuthed} />}
        {view === "list" && (
          <MyEnquiries customer={customer} onNew={() => setView("new")} onOpen={() => setView("detail")} setView={setView} />
        )}
        {view === "new" && (
          <NewEnquiry onDone={(ref) => { window.__lastRef = ref; setView("confirm"); }} onBack={() => setView("list")} />
        )}
        {view === "confirm" && (
          <Confirmation refNo={window.__lastRef} onView={() => setView("detail-ref")} onBack={() => setView("list")} setView={setView} />
        )}
        {view === "detail-ref" && (
          <Detail refNo={window.__lastRef} onBack={() => setView("list")} />
        )}
      </main>
    </div>
  );
}

function Field({ label, required, children, error, testid }) {
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">
        {label} {required && <span className="text-rose-500">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && <p data-testid={testid} className="text-xs text-rose-600 mt-1">{error}</p>}
    </div>
  );
}

function AuthPanel({ onAuthed }) {
  const [mode, setMode] = useState("login"); // login | register
  const [reg, setReg] = useState(EMPTY_REG);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => { enqApi.get("/geo/states").then(({ data }) => setStates(data)).catch(() => setStates([])); }, []);
  useEffect(() => {
    if (!reg.state_code) { setCities([]); return; }
    enqApi.get("/geo/cities", { params: { state_code: reg.state_code } }).then(({ data }) => setCities(data)).catch(() => setCities([]));
  }, [reg.state_code]);

  const submitRegister = async () => {
    const e = {};
    if (!reg.name.trim()) e.name = "Contact person name is required";
    if (!reg.company.trim()) e.company = "Company / firm name is required";
    if (!/^[0-9+\-\s]{7,15}$/.test(reg.mobile.trim())) e.mobile = "Enter a valid mobile number";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reg.email.trim())) e.email = "Enter a valid email";
    if (reg.password.length < 6) e.password = "Password must be at least 6 characters";
    if (!reg.state_code) e.state = "Select your state";
    if (!reg.city.trim()) e.city = "Select your city";
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await enqApi.post("/auth/register", { ...reg, email: reg.email.trim().toLowerCase() });
      setRegistered(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };

  const submitLogin = async () => {
    setBusy(true);
    try {
      const { data } = await enqApi.post("/auth/login", { email: login.email.trim().toLowerCase(), password: login.password });
      onAuthed(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };

  if (registered) {
    return (
      <div data-testid="enq-pending-panel" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 max-w-md mx-auto text-center">
        <div className="w-14 h-14 mx-auto rounded-full bg-amber-100 grid place-items-center mb-4">
          <CheckCircle2 className="w-8 h-8 text-amber-600" />
        </div>
        <h3 className="font-display font-bold text-lg text-slate-800">Registration received</h3>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">
          Thanks for registering with the DVEPL Enquiry Portal. A DVEPL admin will review and approve your account shortly.
          You'll be able to log in and submit tender enquiries once it's approved.
        </p>
        <Button data-testid="enq-pending-back" onClick={() => { setRegistered(false); setReg(EMPTY_REG); setMode("login"); }}
          className="mt-6 h-11 rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] font-semibold px-6">
          Back to Login
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8 max-w-md mx-auto">
      <div className="flex rounded-xl bg-slate-100 p-1 mb-6">
        <button data-testid="enq-tab-login" onClick={() => setMode("login")}
          className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
          Login
        </button>
        <button data-testid="enq-tab-register" onClick={() => setMode("register")}
          className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}>
          Register
        </button>
      </div>

      {mode === "login" ? (
        <div className="space-y-4" data-testid="enq-login-form">
          <Field label="Email" required>
            <Input data-testid="enq-login-email" type="email" value={login.email}
              onChange={(e) => setLogin({ ...login, email: e.target.value })} placeholder="you@company.com" className="h-11" />
          </Field>
          <Field label="Password" required>
            <Input data-testid="enq-login-password" type="password" value={login.password}
              onChange={(e) => setLogin({ ...login, password: e.target.value })} placeholder="••••••" className="h-11"
              onKeyDown={(e) => e.key === "Enter" && submitLogin()} />
          </Field>
          <Button data-testid="enq-login-submit" onClick={submitLogin} disabled={busy}
            className="w-full h-11 rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login"}
          </Button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="enq-register-form">
          <Field label="Contact person full name" required error={errors.name} testid="enq-err-name">
            <Input data-testid="enq-reg-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} className="h-11" />
          </Field>
          <Field label="Company / firm name" required error={errors.company} testid="enq-err-company">
            <Input data-testid="enq-reg-company" value={reg.company} onChange={(e) => setReg({ ...reg, company: e.target.value })} className="h-11" />
          </Field>
          <Field label="Mobile number" required error={errors.mobile} testid="enq-err-mobile">
            <Input data-testid="enq-reg-mobile" value={reg.mobile} onChange={(e) => setReg({ ...reg, mobile: e.target.value })} className="h-11" />
          </Field>
          <Field label="Email" required error={errors.email} testid="enq-err-email">
            <Input data-testid="enq-reg-email" type="email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} className="h-11" />
          </Field>
          <Field label="Password" required error={errors.password} testid="enq-err-password">
            <Input data-testid="enq-reg-password" type="password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} className="h-11" />
          </Field>
          <Field label="State" required error={errors.state} testid="enq-err-state">
            <Combobox testid="enq-reg-state" placeholder="Select your state" searchPlaceholder="Search state…"
              value={reg.state_code}
              options={states.map((s) => ({ value: s.code, label: s.name }))}
              onChange={(code, opt) => setReg({ ...reg, state_code: code, state: opt?.label || "", city: "" })} />
          </Field>
          <Field label="City" required error={errors.city} testid="enq-err-city">
            <Combobox testid="enq-reg-city" placeholder={reg.state_code ? "Select your city" : "Select a state first"}
              searchPlaceholder="Search city…" disabled={!reg.state_code}
              value={reg.city}
              options={cities.map((c) => ({ value: c.name, label: c.name }))}
              onChange={(name) => setReg({ ...reg, city: name })}
              emptyText="No city found" />
          </Field>
          <Button data-testid="enq-register-submit" onClick={submitRegister} disabled={busy}
            className="w-full h-11 rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
          </Button>
        </div>
      )}
    </div>
  );
}

function MyEnquiries({ customer, onNew, setView }) {
  const [items, setItems] = useState(null);
  useEffect(() => { enqApi.get("/enquiries").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);

  return (
    <div data-testid="enq-dashboard">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-xl font-bold text-slate-800">Welcome, {customer?.name}</h1>
          <p className="text-sm text-slate-500">{customer?.company}</p>
        </div>
        <Button data-testid="enq-new-btn" onClick={onNew} className="rounded-full bg-[#22a722] hover:bg-[#1c8f1c] font-semibold shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> New enquiry
        </Button>
      </div>

      {items === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#22a722]" /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
          <FileText className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="mt-3 text-slate-600 font-medium">No enquiries yet</p>
          <p className="text-sm text-slate-400">Submit your first tender enquiry to get started.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="enq-list">
          {items.map((it) => (
            <button key={it.id} data-testid={`enq-item-${it.ref}`}
              onClick={() => { window.__lastRef = it.ref; setView("detail-ref"); }}
              className="w-full text-left bg-white rounded-xl border border-slate-200 hover:border-[#22a722]/50 hover:shadow-sm transition-all p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm font-bold text-[#22a722]">{it.ref}</span>
                <StatusBadge status={it.status} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-700"><Building2 className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{it.department}</span>
                <span className="text-slate-700"><Hash className="w-3.5 h-3.5 inline mr-1 text-slate-400" />{it.tender_id || "—"}</span>
                <span className="text-slate-500"><Calendar className="w-3.5 h-3.5 inline mr-1 text-slate-400" />Due {fmtDMY(it.due_date)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewEnquiry({ onDone, onBack }) {
  const [depts, setDepts] = useState([]);
  const [form, setForm] = useState(EMPTY_ENQ);
  const [dup, setDup] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { enqApi.get("/departments").then(({ data }) => setDepts(data)).catch(() => {}); }, []);

  const checkDup = async (dept, tid) => {
    if (!dept || !tid.trim()) { setDup(false); return; }
    try {
      const { data } = await enqApi.get("/check-duplicate", { params: { department: dept, tender_id: tid.trim() } });
      setDup(!!data.duplicate);
    } catch { setDup(false); }
  };

  const submit = async () => {
    if (!form.department) return toast.error("Please select a department");
    setBusy(true);
    try {
      const { data } = await enqApi.post("/enquiries", { ...form, tender_id: form.tender_id.trim() });
      onDone(data.enquiry.ref);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not submit enquiry"); }
    finally { setBusy(false); }
  };

  const past = isPastDate(form.due_date);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7" data-testid="enq-new-form">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <h1 className="font-display text-xl font-bold text-slate-800 mb-5">New Enquiry</h1>

      <div className="space-y-5">
        <Field label="Enquiry type" required>
          <div className="grid grid-cols-2 gap-3">
            <button type="button" data-testid="enq-type-government"
              onClick={() => setForm({ ...form, type: "government" })}
              className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${form.type === "government" ? "border-[#22a722] bg-[#22a722] text-white" : "border-slate-300 text-slate-600"}`}>
              Government
            </button>
            <button type="button" data-testid="enq-type-private" disabled
              className="h-11 rounded-lg border border-dashed border-slate-300 text-sm font-medium text-slate-400 cursor-not-allowed flex flex-col items-center justify-center leading-tight">
              Private
              <span className="text-[10px]">Coming in next phase</span>
            </button>
          </div>
        </Field>

        <Field label="Department" required>
          <DeptCombobox departments={depts} value={form.department} testid="enq-dept-trigger"
            onChange={(v) => { setForm({ ...form, department: v }); checkDup(v, form.tender_id); }} />
        </Field>

        <Field label="Tender ID">
          <Input data-testid="enq-tender-id" value={form.tender_id} className="h-11"
            onChange={(e) => setForm({ ...form, tender_id: e.target.value })}
            onBlur={(e) => checkDup(form.department, e.target.value)} placeholder="e.g. NIT-2026/123" />
          {dup && (
            <p data-testid="enq-dup-warning" className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> You have already submitted this tender ID for this department.
            </p>
          )}
        </Field>

        <Field label="Enquiry / job due date">
          <Input data-testid="enq-due-date" type="date" value={form.due_date} className="h-11"
            onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          {past && (
            <p data-testid="enq-pastdate-warning" className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> This date is in the past. You can still submit.
            </p>
          )}
        </Field>

        <Field label="Brief requirement / remarks">
          <Textarea data-testid="enq-remarks" rows={4} value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })} placeholder="Describe your requirement (optional)…" />
        </Field>

        <Button data-testid="enq-submit-btn" onClick={submit} disabled={busy}
          className="w-full h-12 rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] font-semibold text-base">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit enquiry"}
        </Button>
      </div>
    </div>
  );
}

function Confirmation({ refNo, onBack, setView }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center max-w-md mx-auto" data-testid="enq-confirmation">
      <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto">
        <CheckCircle2 className="w-9 h-9 text-[#22a722]" />
      </div>
      <h1 className="font-display text-xl font-bold text-slate-800 mt-4">Enquiry submitted!</h1>
      <p className="text-sm text-slate-500 mt-1">Your reference number is</p>
      <p data-testid="enq-confirm-ref" className="font-mono text-2xl font-bold text-[#22a722] mt-1">{refNo}</p>
      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button data-testid="enq-confirm-view" onClick={() => setView("detail-ref")}
          variant="outline" className="flex-1 h-11 rounded-lg">
          <Eye className="w-4 h-4 mr-1.5" /> View enquiry
        </Button>
        <Button data-testid="enq-confirm-back" onClick={onBack}
          className="flex-1 h-11 rounded-lg bg-[#22a722] hover:bg-[#1c8f1c] font-semibold">
          Back to my enquiries
        </Button>
      </div>
    </div>
  );
}

function Detail({ refNo, onBack }) {
  const [enq, setEnq] = useState(null);
  useEffect(() => { enqApi.get(`/enquiries/${refNo}`).then(({ data }) => setEnq(data)).catch(() => setEnq(false)); }, [refNo]);

  if (enq === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#22a722]" /></div>;
  if (enq === false) return <div className="text-center text-slate-500 py-16">Enquiry not found.</div>;

  const rows = [
    ["Reference", enq.ref], ["Type", "Government"], ["Department", enq.department],
    ["Tender ID", enq.tender_id || "—"], ["Due date", fmtDMY(enq.due_date)],
    ["Remarks", enq.remarks || "—"],
  ];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-7" data-testid="enq-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to my enquiries
      </button>
      <div className="flex items-center justify-between mb-5">
        <span className="font-mono text-lg font-bold text-[#22a722]">{enq.ref}</span>
        <StatusBadge status={enq.status} testid="enq-detail-status" />
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v]) => (
          <div key={k} className="py-3 grid grid-cols-3 gap-3">
            <dt className="text-sm text-slate-500">{k}</dt>
            <dd className="col-span-2 text-sm text-slate-800 whitespace-pre-wrap">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
